import type {
  SpeechRecognition,
  SpeechRecognitionError,
  SpeechRecognitionResultEvent,
} from './index.h'

import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { persist } from 'effector-storage/local'
import { model as i18nModel } from '~/core/i18n'
import { t } from '~/core/i18n/i18next'
import { $t } from '~/core/i18n/model'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { model as languagesModel } from '~/entities/languages'
import {
  entitiesNotificationToastsModel,
  NotificationToastType,
} from '~/entities/notification-toasts'
import { noop } from '~/shared/effector'
import {
  SpeechRecognitionErrorType,
  VoiceSearchRecognitionStatus,
} from './index.h'
import { createNotFoundError } from './lib'

export const openVoiceSearchPopup = createEvent()
export const closeVoiceSearchPopup = createEvent()
export const setRecognitionResult = createEvent<string>()
export const setRecognitionStatus = createEvent<VoiceSearchRecognitionStatus>()
export const setRecognitionError = createEvent<string>()
export const setVoiceSearchAvailability = createEvent<boolean>()
export const initVoiceSearch = createEvent()
export const applyVoiceSearchResult = createEvent<string>()
export const changeVoiceSearchLanguage = createEvent<{ '<args>': string[] }>()

const interruptRecognitionForLanguageChange = createEvent<string>()

const $voiceSearchActiveLanguageId = createStore<string | null>(null).on(
  changeVoiceSearchLanguage,
  (_, data) => data['<args>']?.[0] || null
)

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $voiceSearchActiveLanguageId,
  key: 'tria__voice-search-lang',
  fail: noop,
})

const $voiceSearchAvailable = restore(
  setVoiceSearchAvailability,
  typeof window === 'undefined' ||
    Boolean(globalThis.SpeechRecognition) ||
    Boolean(globalThis.webkitSpeechRecognition)
)

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $voiceSearchAvailable,
  key: 'tria__voice-search-available',
  fail: noop,
})

export const $voiceSearchActiveLanguage = combine(
  languagesModel.$languages,
  $voiceSearchActiveLanguageId,
  i18nModel.$locale,
  (languages, activeLanguageId, appLocale) =>
    languages?.find(({ id }) =>
      activeLanguageId ? id === activeLanguageId : id === appLocale
    ) ?? null
)

const checkMicrophonePermissionFx = createEffect<void, boolean>(async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return false
  }

  // Check permission via Permissions API (if available)
  let hasPermission = false
  if (navigator.permissions && navigator.permissions.query) {
    const result = await navigator.permissions.query({ name: 'microphone' })
    if (result.state === 'granted') {
      hasPermission = true
    } else if (result.state === 'denied') {
      entitiesNotificationToastsModel.showToast({
        message: t('microphone_access_browser_notification'),
        type: NotificationToastType.ERROR,
      })
      return false
    }
  }

  // If permission is already granted, check for devices via enumerateDevices
  if (hasPermission) {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const audioInputs = devices.filter((device) => device.kind === 'audioinput')

    if (audioInputs.length) {
      return true
    }

    // No microphone devices found
    throw createNotFoundError()
  }

  // If permission is not granted or failed to check via enumerateDevices,
  // try to request access via getUserMedia
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

  // If access is granted, check for tracks (devices)
  const audioTracks = stream.getAudioTracks()
  stream.getTracks().forEach((track) => track.stop())

  if (audioTracks.length) {
    return true
  }

  // No microphone devices found
  throw createNotFoundError()
})

const openPopupAfterPermissionCheck = createEvent()

const $voiceSearchPopup = createStore<boolean>(false)
  .on(openPopupAfterPermissionCheck, () => true)
  .on(closeVoiceSearchPopup, () => false)

const $recognitionStatus = createStore<VoiceSearchRecognitionStatus>(
  VoiceSearchRecognitionStatus.IDLE
)
  .on(setRecognitionStatus, (_, status) => status)
  .reset(closeVoiceSearchPopup)

const $isListening = $recognitionStatus.map(
  (status) =>
    status === VoiceSearchRecognitionStatus.LISTENING ||
    status === VoiceSearchRecognitionStatus.PROCESSING
)

const $hasError = $recognitionStatus.map(
  (status) => status === VoiceSearchRecognitionStatus.ERROR
)

const $canRetryVoiceSearch = combine(
  $hasError,
  $voiceSearchAvailable,
  (hasError, isAvailable) => hasError && isAvailable
)

const $recognitionResult = createStore('')
  .on(setRecognitionResult, (_, result) => result)
  .reset(closeVoiceSearchPopup)
  .reset(changeVoiceSearchLanguage)

const $recognitionError = createStore('')
  .on(setRecognitionError, (_, error) => error)
  .reset(closeVoiceSearchPopup)

export const $voiceSearchStatus = combine(
  $recognitionError,
  $t,
  (error, { t: translate }) => error || translate('listening_notification')
)

const $voiceSearchLanguages = combine(
  languagesModel.$languages,
  $hasError,
  $recognitionError,
  (languages, hasError, recognitionError) =>
    hasError || recognitionError ? null : languages
)

let currentRecognition: SpeechRecognition | null = null

let isInterruptedByLanguageChange = false

export const stopRecognitionFx = createEffect(() => {
  if (currentRecognition) {
    currentRecognition.stop()
    currentRecognition = null
  }
})

sample({
  clock: closeVoiceSearchPopup,
  target: stopRecognitionFx,
})

// Handle errors in stopRecognitionFx - just reset currentRecognition
sample({
  clock: stopRecognitionFx.failData,
  fn: () => {
    currentRecognition = null
  },
  target: noop,
})

// Stop recognition when language changes during listening
sample({
  clock: changeVoiceSearchLanguage,
  source: $recognitionStatus,
  filter: (status) =>
    status === VoiceSearchRecognitionStatus.LISTENING ||
    status === VoiceSearchRecognitionStatus.PROCESSING,
  fn: (_, data) => data['<args>']?.[0] || '',
  target: interruptRecognitionForLanguageChange,
})

sample({
  clock: interruptRecognitionForLanguageChange,
  fn: () => {
    isInterruptedByLanguageChange = true
  },
  target: stopRecognitionFx,
})

sample({
  clock: interruptRecognitionForLanguageChange,
  fn: () => t('cancel_web_notification'),
  target: setRecognitionError,
})

sample({
  clock: interruptRecognitionForLanguageChange,
  fn: () => VoiceSearchRecognitionStatus.ERROR,
  target: setRecognitionStatus,
})

// Clear recognition result when interrupted due to language change
sample({
  clock: interruptRecognitionForLanguageChange,
  fn: () => '',
  target: setRecognitionResult,
})

// Effect for speech recognition via Web Speech API
export const recognizeSpeechFx = attach({
  source: {
    currentLanguageId: $voiceSearchActiveLanguageId,
  },
  effect: ({ currentLanguageId }) => {
    return new Promise<string>((resolve, reject) => {
      // Check Web Speech API support
      const SpeechRecognition =
        globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition

      if (!SpeechRecognition) {
        setVoiceSearchAvailability(false)
        const error = 'Speech recognition is not supported in this browser'
        reject(new Error(error))
        return
      }

      const recognition = new SpeechRecognition()

      currentRecognition = recognition

      // Recognition settings
      recognition.continuous = false // Stop after first phrase
      recognition.interimResults = true // Enable interim results for dynamic display
      recognition.lang = currentLanguageId || undefined // Recognition language from selected language
      recognition.maxAlternatives = 1

      let finalTranscript = ''

      recognition.onstart = () => {
        setRecognitionStatus(VoiceSearchRecognitionStatus.LISTENING)
        setRecognitionError('')
        setRecognitionResult('')
        finalTranscript = ''
        isInterruptedByLanguageChange = false
      }

      recognition.onresult = (event: SpeechRecognitionResultEvent) => {
        let interimTranscript = ''
        let newFinalTranscript = ''

        // Process all results starting from current index
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript

          if (event.results[i].isFinal) {
            // Final result - add to accumulated final text
            newFinalTranscript += transcript + ' '
          } else {
            // Interim result - show only current one
            interimTranscript += transcript
          }
        }

        // Accumulate final results
        if (newFinalTranscript) {
          finalTranscript += newFinalTranscript
          finalTranscript = finalTranscript.trim()
        }

        // Update result for display (all final + current interim)
        const displayText = interimTranscript
          ? (finalTranscript + ' ' + interimTranscript).trim()
          : finalTranscript.trim()

        if (displayText || interimTranscript) {
          setRecognitionResult(displayText || interimTranscript)
        }
      }

      recognition.onerror = (event: SpeechRecognitionError) => {
        if (currentRecognition === recognition) {
          currentRecognition = null
        }

        let errorMessage: string
        switch (event.error) {
          case SpeechRecognitionErrorType.NO_SPEECH:
            errorMessage = t('silence_web_notification')
            break
          case SpeechRecognitionErrorType.NOT_ALLOWED:
            errorMessage =
              'Microphone access denied. Please allow microphone access.'
            entitiesNotificationToastsModel.showToast({
              message: t('microphone_access_browser_notification'),
              type: NotificationToastType.ERROR,
            })
            break
          case SpeechRecognitionErrorType.SERVICE_NOT_ALLOWED:
            errorMessage = t('voice_search_unavailable_notification')
            setVoiceSearchAvailability(false)
            entitiesNotificationToastsModel.showToast({
              message: t('voice_search_unavailable_notification'),
              type: NotificationToastType.ERROR,
            })
            break
          case SpeechRecognitionErrorType.ABORTED:
            errorMessage = 'Recognition aborted.'
            break
          default:
            errorMessage = `Recognition error: ${event.error}`
        }

        setRecognitionResult('')
        setRecognitionError(errorMessage)
        setRecognitionStatus(VoiceSearchRecognitionStatus.ERROR)
        reject(new Error(errorMessage))

        // If the error is due to service not allowed, close the popup immediately
        if (event.error === SpeechRecognitionErrorType.SERVICE_NOT_ALLOWED) {
          closeVoiceSearchPopup()
        }
      }

      recognition.onend = () => {
        if (currentRecognition === recognition) {
          currentRecognition = null
        }

        // If recognition was interrupted due to language change, don't treat as error
        // (error message already set in interruptRecognitionForLanguageChange)
        if (isInterruptedByLanguageChange) {
          isInterruptedByLanguageChange = false
          return
        }

        if (finalTranscript) {
          setRecognitionStatus(VoiceSearchRecognitionStatus.PROCESSING)
          resolve(finalTranscript)
        } else {
          setRecognitionStatus(VoiceSearchRecognitionStatus.ERROR)
          setRecognitionError(t('silence_web_notification'))
        }
      }

      try {
        recognition.start()
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to start recognition'
        setRecognitionError(errorMessage)
        setRecognitionStatus(VoiceSearchRecognitionStatus.ERROR)
        reject(new Error(errorMessage))
      }
    })
  },
})

sample({
  clock: recognizeSpeechFx.failData,
  fn: (error) => error.message,
  target: setRecognitionError,
})

// Check microphone permission before opening popup
sample({
  clock: openVoiceSearchPopup,
  source: $voiceSearchAvailable,
  filter: Boolean,
  fn: () => undefined,
  target: checkMicrophonePermissionFx,
})

sample({
  clock: openVoiceSearchPopup,
  source: $voiceSearchAvailable,
  filter: (isAvailable) => !isAvailable,
  fn: () => ({
    message: t('voice_search_unavailable_notification'),
    type: NotificationToastType.ERROR,
  }),
  target: entitiesNotificationToastsModel.showToast,
})

// Handle errors in checkMicrophonePermissionFx
sample({
  clock: checkMicrophonePermissionFx.failData,
  filter: (error) =>
    error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError',
  fn: () => ({
    message: t('microphone_access_browser_notification'),
    type: NotificationToastType.ERROR,
  }),
  target: entitiesNotificationToastsModel.showToast,
})

// Handle NotFoundError when microphone is not found
sample({
  clock: checkMicrophonePermissionFx.failData,
  filter: (error) => error.name === 'NotFoundError',
  fn: () => ({
    message: t('microphone_not_found'),
    type: NotificationToastType.ERROR,
  }),
  target: entitiesNotificationToastsModel.showToast,
})

// Open popup only if permission is granted
sample({
  clock: checkMicrophonePermissionFx.doneData,
  filter: (hasPermission) => hasPermission,
  target: openPopupAfterPermissionCheck,
})

// Set "listening" status immediately when popup opens
sample({
  clock: [openPopupAfterPermissionCheck, initVoiceSearch],
  fn: () => VoiceSearchRecognitionStatus.LISTENING,
  target: setRecognitionStatus,
})

// Automatically start recognition when popup opens
sample({
  clock: [openPopupAfterPermissionCheck, initVoiceSearch],
  target: recognizeSpeechFx,
})

// Delay before closing popup and applying search (so user can see the result)
const DELAY_BEFORE_CLOSE_MS = 1500

const applySearchAndCloseFx = createEffect<string, string>((result) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(result)
    }, DELAY_BEFORE_CLOSE_MS)
  })
})

// Apply voice search result
sample({
  clock: applySearchAndCloseFx.doneData,
  target: applyVoiceSearchResult,
})

// Close popup after delay
sample({
  clock: applySearchAndCloseFx.done,
  target: closeVoiceSearchPopup,
})

// Close popup on location change
sample({
  clock: router.$location,
  target: closeVoiceSearchPopup,
})

// Start delay after successful recognition
sample({
  clock: recognizeSpeechFx.doneData,
  target: applySearchAndCloseFx,
})

register($voiceSearchPopup, '$features/voice-search/popup')
register($recognitionStatus, '$features/voice-search/status')
register($isListening, '$features/voice-search/isListening')
register($hasError, '$features/voice-search/hasError')
register($voiceSearchAvailable, '$features/voice-search/isAvailable')
register($canRetryVoiceSearch, '$features/voice-search/canRetry')
register($recognitionError, '$features/voice-search/error')
register($voiceSearchStatus, '$features/voice-search/displayStatus')
register($recognitionResult, '$features/voice-search/displayResult')
register($voiceSearchLanguages, '$features/voice-search/languages')
register($voiceSearchActiveLanguage, '$features/voice-search/activeLanguage')
register(openVoiceSearchPopup, 'features/voice-search/openPopup')
register(closeVoiceSearchPopup, 'features/voice-search/closePopup')
register(setRecognitionResult, 'features/voice-search/setResult')
register(initVoiceSearch, 'features/voice-search/initVoiceSearch')
register(changeVoiceSearchLanguage, 'features/voice-search/changeLanguage')
register(applyVoiceSearchResult, 'features/voice-search/applyResult')
